import {
  DeleteOutlined,
  EyeOutlined,
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { request } from "@umijs/max";
import { Image, message, Tooltip, Upload } from "antd";
import type {
  UploadChangeParam,
  UploadFile,
  UploadProps,
} from "antd/es/upload";
import type { RcFile } from "antd/es/upload/interface";
import React, { useState } from "react";
import { generatePresignedUrl, getDownloadUrl } from "@/services/app-api/file";

interface FileUploadConfig {
  maxSize: number; // MB
  allowedTypes: string[];
  accept: string;
  listType?: "text" | "picture" | "picture-card";
  uploadText?: string;
  helpText?: string;
  showPreview?: boolean;
}

interface FileUploadProps {
  value?: AppAPI.FileInfo;
  onChange?: (fileInfo: AppAPI.FileInfo | undefined) => void;
  disabled?: boolean;
  config: FileUploadConfig;
}

/**
 * 通用文件上传组件
 * 支持图片、PDF等多种文件类型上传
 */
const FileUpload: React.FC<FileUploadProps> = ({
  value,
  onChange,
  disabled = false,
  config,
}) => {
  const [loading, setLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);

  // 从 value 计算文件信息
  const hasFile = !!value;
  const isImage = value?.mimeType?.startsWith("image/");
  const imageUrl =
    isImage && value?.downloadUrl ? value.downloadUrl : undefined;
  const fileName = value?.fileName || "";
  const fileSize = value?.fileSize
    ? `(${(value.fileSize / 1024 / 1024).toFixed(2)}MB)`
    : "";

  // 当 value 变化时，重置图片加载错误状态
  React.useEffect(() => {
    if (imageUrl) {
      setImageLoadError(false);
    }
  }, [imageUrl]);

  /**
   * 文件上传前的验证
   */
  const beforeUpload = (file: RcFile) => {
    const isAllowedType = config.allowedTypes.includes(file.type);
    if (!isAllowedType) {
      const typeNames = config.allowedTypes
        .map((type) => {
          if (type === "application/pdf") return "PDF";
          if (type === "image/jpeg") return "JPG";
          if (type === "image/png") return "PNG";
          return type;
        })
        .join("、");
      message.error(`只能上传 ${typeNames} 格式的文件!`);
      return false;
    }

    const isLtMaxSize = file.size / 1024 / 1024 < config.maxSize;
    if (!isLtMaxSize) {
      message.error(`文件大小不能超过 ${config.maxSize}MB!`);
      return false;
    }

    return true;
  };

  /**
   * 自定义上传处理函数
   */
  const customRequest = async (options: any) => {
    const { file, onSuccess, onError, onProgress } = options;

    setLoading(true);

    try {

      const presignedResponse = await generatePresignedUrl({
        fileName: file.name,
        contentType: file.type,
      });

      if (!presignedResponse.success || !presignedResponse.data) {
        throw new Error("获取上传地址失败");
      }

      const { uploadUrl, fileKey } = presignedResponse.data;

      await request(uploadUrl, {
        method: "PUT",
        data: file,
        headers: {
          "Content-Type": file.type,
        },
        // 监听上传进度
        onUploadProgress: (event: ProgressEvent) => {
          if (event.lengthComputable && onProgress) {
            const percent = Math.round((event.loaded / event.total) * 100);
            onProgress({ percent });
          }
        },
      });

      // 4. 获取文件的下载签名URL
      const downloadUrlResponse = await getDownloadUrl(fileKey);

      if (!downloadUrlResponse.success || !downloadUrlResponse.data) {
        throw new Error("获取下载地址失败");
      }

      // 5. 构造文件信息对象
      const fileInfo: AppAPI.FileInfo = {
        fileKey: fileKey,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        downloadUrl: downloadUrlResponse.data.downloadUrl,
      };

      // 6. 更新组件状态和回调
      onChange?.(fileInfo);

      onSuccess({
        url: fileInfo.downloadUrl,
        fileInfo,
      });
      message.success("文件上传成功");
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "文件上传失败，请重试";
      message.error(errorMessage);
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 上传状态变化处理
   */
  const handleChange: UploadProps["onChange"] = (
    info: UploadChangeParam<UploadFile>,
  ) => {
    if (info.file.status === "uploading") {
      setLoading(true);
    } else if (info.file.status === "done") {
      setLoading(false);
    } else if (info.file.status === "error") {
      setLoading(false);
      message.error("文件上传失败，请重试");
    }
  };

  /**
   * 删除文件
   */
  const handleRemove = () => {
    onChange?.(undefined);
    return true;
  };

  /**
   * 预览文件
   */
  const handlePreview = () => {
    if (value?.downloadUrl) {
      if (isImage && config.showPreview) {
        setPreviewVisible(true);
      } else {
        window.open(value.downloadUrl, "_blank");
      }
    }
  };

  const uploadButton = (
    <div
      style={{
        textAlign: "center",
        padding: config.listType === "picture-card" ? "20px" : "8px",
      }}
    >
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>
        {loading ? "上传中..." : config.uploadText || "选择文件"}
      </div>
    </div>
  );

  // 渲染图片预览（picture-card模式）
  const renderImagePreview = () => {
    if (!imageUrl || imageLoadError) return uploadButton;

    return (
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        <img
          src={imageUrl}
          alt="preview"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "6px",
          }}
          onError={() => {
            setImageLoadError(true);
            message.warning("图片加载失败，请重新上传");
          }}
          onLoad={() => {
            setImageLoadError(false);
          }}
        />
        {!disabled && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              opacity: 0,
              transition: "opacity 0.3s",
              cursor: "pointer",
              borderRadius: "6px",
            }}
            className="upload-hover-mask"
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "1";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "0";
            }}
          >
            <Tooltip title="预览">
              <EyeOutlined
                style={{ color: "white", fontSize: "16px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePreview();
                }}
              />
            </Tooltip>
            <Tooltip title="删除">
              <DeleteOutlined
                style={{ color: "white", fontSize: "16px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
              />
            </Tooltip>
            <Tooltip title="重新上传">
              <PlusOutlined style={{ color: "white", fontSize: "16px" }} />
            </Tooltip>
          </div>
        )}
      </div>
    );
  };

  // 渲染文件预览（picture-card模式，非图片文件）
  const renderFilePreview = () => {
    return (
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "8px",
        }}
      >
        <div
          style={{
            fontSize: "24px",
            color: "#1890ff",
            marginBottom: "8px",
          }}
        >
          📄
        </div>
        <div
          style={{
            fontSize: "12px",
            textAlign: "center",
            wordBreak: "break-all",
            lineHeight: "1.2",
          }}
        >
          <div>{fileName}</div>
          <div style={{ color: "#999", marginTop: "4px" }}>{fileSize}</div>
        </div>

        {!disabled && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              opacity: 0,
              transition: "opacity 0.3s",
              cursor: "pointer",
              borderRadius: "6px",
            }}
            className="upload-hover-mask"
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "1";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "0";
            }}
          >
            {/* <Tooltip title="预览">
              <EyeOutlined
                style={{ color: 'white', fontSize: '16px' }}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePreview();
                }}
              />
            </Tooltip> */}
            <Tooltip title="删除">
              <DeleteOutlined
                style={{ color: "white", fontSize: "16px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
              />
            </Tooltip>
            <Tooltip title="重新上传">
              <PlusOutlined style={{ color: "white", fontSize: "16px" }} />
            </Tooltip>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <Upload
        name="file"
        listType={config.listType || "picture-card"}
        className="file-uploader"
        showUploadList={false}
        beforeUpload={beforeUpload}
        customRequest={customRequest}
        onChange={handleChange}
        onRemove={handleRemove}
        disabled={disabled}
        accept={config.accept}
      >
        {hasFile
          ? config.listType === "picture-card"
            ? isImage
              ? renderImagePreview()
              : renderFilePreview()
            : uploadButton
          : uploadButton}
      </Upload>

      {config.helpText && (
        <div style={{ marginTop: 4, fontSize: "12px", color: "#999" }}>
          {config.helpText}
        </div>
      )}

      {/* 图片预览模态框 */}
      {imageUrl && config.showPreview && (
        <Image
          width={0}
          height={0}
          style={{ display: "none" }}
          preview={{
            visible: previewVisible,
            onVisibleChange: setPreviewVisible,
            src: imageUrl,
            mask: false,
          }}
        />
      )}
    </div>
  );
};

export default FileUpload;
